import { allFields, formatNumber, humanize, visibilityOf } from './schema';
import type { DataClassRecord, DataFieldRecord, DataRecord, OwnedField, Visibility } from './types';

// The category table (balancing view): one row per record, one column per simple visible field.
// Pure helpers so sorting and statistics are unit-tested (table.test.ts).

/** Kinds that fit in one table cell. Lists, nested classes, assets and long text stay in the record view. */
const CELL_KINDS = ['int', 'float', 'bool', 'string', 'enum', 'flags', 'ref'];

export interface Column {
  field: OwnedField;
  visibility: Visibility;
  numeric: boolean;
}

/**
 * Columns for these records: every simple, non-hidden field of their classes (a category can mix
 * subclasses, e.g. items and food), base-class fields first. The name field is the row header already.
 */
export function tableColumns(
  classes: Record<string, DataClassRecord>,
  settings: Record<string, DataFieldRecord>,
  records: DataRecord[],
  nameField?: string,
): Column[] {
  const seen = new Set<string>();
  const out: Column[] = [];
  const classNames = [...new Set(records.map((r) => r.className))];
  // Base classes first: the class with the shortest chain of fields wins the order.
  classNames.sort((a, b) => allFields(classes, a).length - allFields(classes, b).length);
  for (const cn of classNames) {
    for (const f of allFields(classes, cn)) {
      const id = f.owner + '|' + f.name;
      if (seen.has(id)) continue;
      seen.add(id);
      if (!CELL_KINDS.includes(f.kind) || f.name === nameField) continue;
      if (f.kind === 'string' && f.multiline) continue;
      const visibility = visibilityOf(f, settings);
      if (visibility === 'hidden') continue;
      out.push({ field: f, visibility, numeric: f.kind === 'int' || f.kind === 'float' });
    }
  }
  return out;
}

/** A comparable key for sorting: numbers as numbers, everything else as text; missing sorts last. */
export function sortKey(v: unknown): number | string | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (Array.isArray(v)) return v.map((x) => String(x)).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function compareValues(a: unknown, b: unknown): number {
  const x = sortKey(a);
  const y = sortKey(b);
  if (x === null && y === null) return 0;
  if (x === null) return 1;
  if (y === null) return -1;
  if (typeof x === 'number' && typeof y === 'number') return x - y;
  return String(x).localeCompare(String(y), 'sv', { numeric: true });
}

export interface Sort {
  field: string | null; // null = by name
  dir: 1 | -1;
}

export function sortRecords(records: DataRecord[], sort: Sort): DataRecord[] {
  return [...records].sort((a, b) => {
    const c = sort.field
      ? compareValues(a.values?.[sort.field], b.values?.[sort.field])
      : a.name.localeCompare(b.name, 'sv', { numeric: true });
    // Missing values stay at the bottom in both directions.
    if (
      sort.field &&
      (sortKey(a.values?.[sort.field]) === null) !== (sortKey(b.values?.[sort.field]) === null)
    )
      return c;
    return c * sort.dir || a.name.localeCompare(b.name, 'sv');
  });
}

export interface Stats {
  min: number;
  median: number;
  max: number;
}

/** Min / median / max of a numeric column over the shown rows (null when no row has a number). */
export function columnStats(records: DataRecord[], field: string): Stats | null {
  const xs = records
    .map((r) => r.values?.[field])
    .filter((v): v is number => typeof v === 'number' && isFinite(v))
    .sort((a, b) => a - b);
  if (!xs.length) return null;
  const mid = Math.floor(xs.length / 2);
  const median = xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
  return { min: xs[0], median, max: xs[xs.length - 1] };
}

/** Short plain text for a cell's tooltip ("Unity: 20"). */
export function cellText(
  v: unknown,
  refName: (ref: { guid?: string; new?: string }) => string | undefined,
): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') return formatNumber(v);
  if (typeof v === 'boolean') return v ? '✓' : '—';
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'string' ? humanize(x) : String(x))).join(', ');
  if (typeof v === 'object') return refName(v as { guid?: string; new?: string }) ?? '?';
  return String(v);
}
